// Host / Stop hosting, inside the Build panel (task 2026-10-05 "a built database is hosted
// under its slug with one Host button"). GET /projects/{id}/hosting says everything shown here;
// the address is ALWAYS the backend's `url` (services/addresses.project_url) and never put
// together in the browser, so the dialog, the project badge and the project host agree.
//
//   not hosted, a built version, a project manager -> Host (disabled with the backend's reason
//     when the database is gone or stale) -> HostDialog explains where, which version, what
//     changes and who gets access -> POST .../hosting {version}. With project addresses off
//     (`url` null) "where" is the portal's own Data pages, /projects/<id>/data (decision O16).
//   hosted -> "Hosted at <url>" with Copy; an admin gets Stop hosting -> ConfirmDialog -> DELETE
//   an older built version (`rebuildable`, task 2026-10-12) -> the dialog's version picker says
//     "version k is rebuilt as version n+1, then hosted" -> POST .../hosting {version: k,
//     rebuild: true} (202) -> the panel follows version n+1 until it is hosted or failed.
//
// Focus (frontend/src/focus.js): the Host button disappears once the project is hosted and
// Stop hosting once it is not, so focus goes back to the button that opened the dialog, or else
// to the other state's first control, never to <body>.
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import { Btn, ConfirmDialog, Spinner, useModalFocus } from "../../components/Shared";
import { ErrorNote } from "../../components/Feedback";
import { rememberFocus } from "../../focus";

export const POLL_MS = 2000;
const mono = { fontFamily: "'JetBrains Mono', monospace" };
const when = t => (t ? new Date(t).toLocaleString() : "");

export function CopyButton({ text, label = "Copy" }) {
  const [done, setDone] = useState(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone("Copied.");
    } catch {
      setDone("Copying failed; select the address and copy it by hand.");
    }
  };
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      <Btn variant="secondary" style={{ padding: "4px 10px", fontSize: 12 }} onClick={copy}>{label}</Btn>
      <span role="status" style={{ fontSize: 12, color: "#374151" }}>{done}</span>
    </span>
  );
}

function rowsTotal(rows) {
  return rows ? Object.values(rows).reduce((a, b) => a + b, 0) : null;
}

// The explaining dialog: BOKU's ConfirmDialog pattern (role=dialog, aria-modal, Escape and a
// click outside close it, focus moves into it) without the type-to-confirm word — hosting is
// reversible by an admin, it does not delete anything. Its keyboard side is ConfirmDialog's
// useModalFocus (boku-ui ce016d0): Tab stays inside, focus goes back to the opener (a11y review
// 2026-10-06; before, Tab walked out to the page behind).
export function HostDialog({ h, busy, error, onConfirm, onClose }) {
  const ref = useRef(null);
  const id = useId();
  const c = h.candidate;
  const older = h.rebuildable || [];
  // the version the database holds, unless it cannot be hosted (gone or stale): then the newest older one
  const [choice, setChoice] = useState(h.available || !older.length ? c.version : older[0].version);
  const rebuild = choice !== c.version;
  const picked = older.find(o => o.version === choice);
  useModalFocus(ref, onClose);
  const total = rowsTotal(c.rows);
  const p = { margin: "0 0 10px", fontSize: 14, color: "#374151" };
  const h3 = { fontSize: 13, margin: "14px 0 6px", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6b7280" };
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 220, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={`${id}-t`} tabIndex={-1}
           style={{ background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb", maxWidth: 600, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: "24px 28px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        <h2 id={`${id}-t`} style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700 }}>Host {h.database_name}?</h2>

        <h3 style={h3}>Where</h3>
        {h.url ? (
          <>
            <p style={p}>The database will be open for data entry at its own address:</p>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <code data-testid="host-url" style={{ ...mono, fontSize: 14, background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 8px" }}>{h.url}</code>
              <CopyButton text={h.url} label="Copy address" />
            </div>
          </>
        ) : (
          <p style={p}>
            The database will be open for data entry on the project's{" "}
            <Link to={`/projects/${h.project_id}/data`}>Data pages</Link> of this portal. Project
            addresses are off, so it gets no address of its own; an admin can switch on project
            addresses later, and it then gets one without anything else changing.
          </p>
        )}

        <h3 style={h3}>Which version</h3>
        {older.length > 0 && (
          <p style={p}>
            <label htmlFor={`${id}-v`} style={{ marginRight: 8 }}>Version to host</label>
            <select id={`${id}-v`} value={choice} onChange={e => setChoice(Number(e.target.value))}>
              <option value={c.version} disabled={!h.available}>Version {c.version} (in {h.database_name} now)</option>
              {older.map(o => (
                <option key={o.version} value={o.version}>
                  Version {o.version}{o.built_at ? `, built ${when(o.built_at)}` : ""} (rebuilt first)
                </option>
              ))}
            </select>
          </p>
        )}
        {rebuild ? (
          <p data-testid="host-rebuild" style={p}>
            Version {choice} is rebuilt as version {h.next_version}, then hosted: its design
            ({picked.table_count} table{picked.table_count === 1 ? "" : "s"}) is copied into a new
            version {h.next_version}, which is built from the files version {choice} was built from and
            replaces what {h.database_name} holds now. When that build has finished, version {h.next_version} is
            hosted. If a file it needs is gone, nothing changes and the reason is shown here.
          </p>
        ) : (
          <p style={p}>
            Version {c.version}{c.built_at ? `, built ${when(c.built_at)}` : ""}: {c.table_count} table{c.table_count === 1 ? "" : "s"}
            {total != null ? `, ${total} row${total === 1 ? "" : "s"}` : ""}. This is the version {h.database_name} holds now,
            the latest built one.{older.length > 0
              ? " An older built version can be chosen above: it is built again as a new version first."
              : ` ${h.database_name} holds only the last build.`}
          </p>
        )}

        <h3 style={h3}>What changes</h3>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: "#374151" }}>
          <li>Build no longer replaces the database: it is refused while the database is hosted, so the
            rows entered stay. A new design version is applied to it as a migration, under "Migrate the
            hosted database" on this page.</li>
          <li>Project members read its tables {h.url ? "at that address and " : ""}on the project's Data pages, each
            with the data role below; editors add, edit and delete rows on the Data pages.</li>
          <li>Personal SQL logins for R, Python and BI tools can be created on the project page.</li>
          <li>Only an admin can stop hosting; after that, a rebuild replaces the data again.</li>
        </ul>

        <h3 style={h3}>Who gets access</h3>
        <ul aria-label="Data roles after hosting" style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: "#374151" }}>
          {h.default_roles.map(r => <li key={r.user_id}>{r.username}: {r.role}</li>)}
        </ul>
        <p style={{ ...p, marginTop: 6, fontSize: 13 }}>The roles can be changed afterwards under Data access on the project page.</p>

        <ErrorNote error={error} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn disabled={busy} onClick={() => onConfirm(choice, rebuild)}>
            {busy ? <><Spinner /> Hosting…</> : rebuild ? `Rebuild version ${choice} and host it` : `Host version ${c.version}`}
          </Btn>
        </div>
      </div>
    </div>
  );
}

export default function HostingPanel({ projectId, refreshKey, onChange }) {
  const path = `/projects/${projectId}/hosting`;
  // Its own state rather than useApi: a reload must never show the previous state again for a
  // moment (the Host button reappearing would take the focus with it when it goes), and the
  // newest answer wins over a slower older one.
  const [h, setH] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const seq = useRef(0);
  const load = useCallback(async () => {
    const n = ++seq.current;
    try {
      const now = await api.get(path);
      if (n === seq.current) { setH(now); setLoadError(null); }
      return now;
    } catch (e) {
      if (n === seq.current) setLoadError(e);
      throw e;
    }
  }, [path]);
  const [dialog, setDialog] = useState(null);       // "host" | "stop" | null
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const restoreRef = useRef(null);
  const restorePending = useRef(false);
  const box = useRef(null);
  const first = () => box.current?.querySelector("a, button:not([disabled])");

  // On mount, and again when a build finished or the workspace's hosted state changed.
  useEffect(() => { load().catch(() => {}); }, [load, refreshKey]);

  // Give focus back only once the dialog is gone AND the new state is on screen: before that,
  // the button that opened the dialog may still be there for a frame and then vanish. The
  // parent's onChange (which reloads the workspace and itself queues focus for its Build
  // button) runs after that, so this panel's own target wins the next animation frame.
  const changed = useRef(false);
  useEffect(() => {
    if (dialog || !restorePending.current) return;
    restorePending.current = false;
    restoreRef.current?.(first);
    if (changed.current) {
      changed.current = false;
      onChange?.();
    }
  }, [dialog, h]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = kind => { restoreRef.current = rememberFocus(); setError(null); setDialog(kind); };
  const close = () => { restorePending.current = true; setDialog(null); };
  const act = async (call) => {
    setBusy(true);
    setError(null);
    try {
      await call();
      await load();
      restorePending.current = true;
      changed.current = true;
      setDialog(null);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  // An older version: a 202 with the new version n+1, which the panel then follows.
  const [rebuilding, setRebuilding] = useState(null);   // {version, from, failed?: string}
  const host = (version = h.candidate.version, rebuild = false) => act(async () => {
    if (!rebuild) return api.post(`/projects/${projectId}/hosting`, { version });
    const v = await api.post(`/projects/${projectId}/hosting`, { version, rebuild: true });
    setRebuilding({ version: v.version, from: version });
  });
  useEffect(() => {
    if (!rebuilding || rebuilding.failed) return undefined;
    let alive = true;
    let timer = null;
    const tick = async () => {
      try {
        const v = await api.get(`/projects/${projectId}/dbdesign/versions/${rebuilding.version}`);
        const now = await load();
        if (!alive) return;
        const lines = (v.build_log || "").split("\n");
        const hostingFailed = lines.find(l => l.startsWith("Hosting failed"));
        if (now.hosted) {
          setRebuilding(null);
          onChange?.();
          return;
        }
        if (v.status === "failed" || hostingFailed) {
          const why = hostingFailed || [...lines].reverse().find(l => l.startsWith("FAILED"));
          setRebuilding(r => ({ ...r, failed: why || "The build failed; its log is in the Build panel." }));
          onChange?.();
          return;
        }
      } catch {
        if (!alive) return;                        // a passing network error: try again
      }
      timer = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, [rebuilding, projectId, load]); // eslint-disable-line react-hooks/exhaustive-deps
  const stop = () => act(() => api.del(`/projects/${projectId}/hosting`));

  if (loadError) return <ErrorNote error={loadError} />;
  if (!h) return null;
  if (!h.hosted && (!h.candidate || !h.can_host)) return null;   // nothing built, or not theirs to host
  const canHost = h.available || (h.rebuildable || []).length > 0;
  const following = rebuilding && !rebuilding.failed;

  return (
    <div ref={box} data-testid="hosting" style={{ marginTop: 14, padding: "12px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#f9fafb", fontSize: 13, color: "#374151" }}>
      {h.hosted ? (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
          <span>
            <strong>Hosted</strong>
            {h.url ? <> at <a href={h.url} style={mono}>{h.url}</a></> : " (project addresses are off, so it has no address of its own)"}
            {`, version ${h.version}`}{h.since ? `, since ${when(h.since)}` : ""}{h.by ? ` by ${h.by}` : ""}.
          </span>
          <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            {h.url && <CopyButton text={h.url} label="Copy address" />}
            {h.can_stop && <Btn variant="secondary" onClick={() => open("stop")}>Stop hosting</Btn>}
          </span>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
          <span>
            Host version {h.candidate.version} to open it for data entry
            {h.url ? <> at <span style={mono}>{h.url}</span></> : ""}.
            {h.reason && <span data-testid="host-reason" style={{ display: "block", marginTop: 4, color: "#92400e" }}>{h.reason}</span>}
            {rebuilding && (
              <span role="status" data-testid="host-rebuilding" style={{ display: "block", marginTop: 4, color: rebuilding.failed ? "#991b1b" : "#374151" }}>
                {rebuilding.failed
                  ? `Version ${rebuilding.from} was not hosted (its rebuild, version ${rebuilding.version}): ${rebuilding.failed}`
                  : <><Spinner /> Version {rebuilding.from} is being rebuilt as version {rebuilding.version}, then hosted…</>}
              </span>
            )}
          </span>
          <Btn onClick={() => open("host")} disabled={!canHost || busy || following}>Host</Btn>
        </div>
      )}
      {!dialog && <ErrorNote error={error} />}
      {dialog === "host" && <HostDialog h={h} busy={busy} error={error} onConfirm={host} onClose={close} />}
      {dialog === "stop" && (
        <ConfirmDialog title={`Stop hosting ${h.database_name}?`} confirmWord={h.database_name}
                       confirmLabel="Stop hosting" onConfirm={stop} onClose={close}>
          <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
            The project returns to design time: {h.url ? <>{h.url} stops opening the database, </> : ""}the
            data roles and personal SQL logins are removed, and the next Build drops {h.database_name} and
            replaces every row entered into it. Download a dump first if the rows are still needed.
          </p>
          {error && <ErrorNote error={error} />}
        </ConfirmDialog>
      )}
    </div>
  );
}
